import { v4 as UUID } from 'uuid';
import DataReference from './DataReference.js';
import DataReferenceQuery from './DataReferenceQuery.js';
/**
 * @memberof data-references
 * @classdesc
 * Represents a reference to one or more variables or a query that evaluates
 * to a collection of zero or more variable references. Used in conjunction
 * with a FeatureReferenceCollection can yield a set of corresponding values.
 *
 * @property {string} id
 * @property {data-references.DataReference[]} variables
 * @property {data-references.DataReferenceQuery} query
 */
class DataReferenceCollection {
    /**
     * @param {object} props
     * @param {string} [props.id]
     * @param {data-references.DataReference[]} [props.variables]
     * @param {data-references.DataReferenceQuery} [props.query]
     */
    constructor(props = {}) {
        this.id = props.id || UUID();
        this.query = new DataReferenceQuery(props.query);
        this.variables = (props.variables || []).map(props => props && new DataReference(props));
    }
    /**
     * @type {boolean}
     */
    get isDynamic() {
        return this.query.hasParameters;
    }
    /**
     * @type {boolean}
     */
    get isStatic() {
        return !this.isDynamic;
    }
    /**
     * @type {string}
     */
    get key() {
        return (this.isStatic
            ? this.variables.map(ref => ref.key).sort().join('+')
            : this.query.key);
    }
    /**
     * @private
     * @description
     * See https://mysidewalk.atlassian.net/browse/PFP-1817
     * and https://github.com/mysidewalk/frontend/pull/984/commits/efd4f885ae46f6d1bcd1e6372e0a88359489a9cc#r228656266
     * for the problem this is able to address.
     */
    _load() {
        let result = Promise.resolve(this);
        if (!this.isStatic) {
            result = this.query.evaluate().then((variables) => {
                this.variables = variables;
                return this;
            });
        }
        return result;
    }
    /**
     * @return {Promise}
     */
    ready() {
        return this._load();
    }
    /**
     * @param {data-references.DataReferenceCollection}
     * @return {boolean}
     */
    isEqual(collection) {
        return this.id === collection.id;
    }
    /**
     * @return {boolean}
     */
    areAllVariablesEqual() {
        const [firstVariable] = this.variables;
        const doesMatchFirst = (variable => firstVariable.isEqual(variable));
        return this.variables.every(doesMatchFirst);
    }
    /**
     * @return {object}
     */
    toJSON() {
        return {
            id: this.id,
            variables: this.query.hasParameters ? [] : this.variables,
            query: this.query.hasParameters ? this.query : {},
        };
    }
    /**
     * @return {object}
     */
    toImplementationJSON() {
        return { id: this.id };
    }
    /**
     * @param {DataReference[]} references
     * @return {DataReferenceCollection}
     */
    static fromDataReferences(references) {
        return new DataReferenceCollection({ variables: references });
    }
    /**
     * @param {DataReferenceCollection[]} dataReferenceCollections
     * @return {string}
     */
    static keyFor(dataReferenceCollections) {
        return (dataReferenceCollections
            .map((collection) => collection.key)
            .sort()
            .join('|'));
    }
}
DataReferenceCollection.DataReference = DataReference;
DataReferenceCollection.Query = DataReferenceQuery;
export default DataReferenceCollection;
